import React from "react";
import useShowLoader from "../../common/loading-widgets/useShowLoader";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import useTranslationCandidates from "../useTranslationCandidates";
import ApplicationPreview from "./ApplicationPreview";
import useMutationApplicationUpdate from "../mutate/useMutationApplicationUpdate";
import useServerErrorFormatter from "../../common/modals/useServerErrorFormatter";
import useErrorModal from "../../common/modals/useErrorModal";
import "./ApplicationsList.scss";
import TypeUtils from "../../../utils/TypeUtils";
import ThreeStateCheckbox from "../../common/components/ThreeStateCheckbox";
import ApplicationStatusEnum from "../ApplicationStatusEnum";

export default function ApplicationsList(props) {
  const { applications, status, onFilterChange } = props;

  // Initialize a map of application selected status by application IDs. Status is initialized to false.
  const updateApplicationsSelectMap = (
    applicationList,
    applicationsSelectMap
  ) => {
    const updatedApplicationsSelectMap = {};

    // Add entries from applicationList. This will ignore entries from applicationsSelectMap that are no longer useful.
    // Re-use select status from applicationsSelectMap if application is not a new one, otherwise default select status
    // for new applications is false.
    TypeUtils.ensureArray(applicationList).forEach((application) => {
      if (applicationsSelectMap[application._id]) {
        updatedApplicationsSelectMap[application._id] =
          applicationsSelectMap[application._id];
      } else {
        updatedApplicationsSelectMap[application._id] = false;
      }
    });

    return updatedApplicationsSelectMap;
  };

  const [selection, setSelection] = React.useState({
    selectAll: false,
    applicationsSelect: updateApplicationsSelectMap(applications, {}),
  });

  // Checkbox status is indeterminate when some applications are selected and some other are not
  const allChecked = Object.values(selection.applicationsSelect).every(isChecked);
  const allUnchecked = Object.values(selection.applicationsSelect).every(isUnchecked);
  const indeterminate = !(allChecked || allUnchecked);

  const { t, loading: tLoading } = useTranslationCandidates();
  useShowLoader(tLoading, "ApplicationsList");

  const STATUS_ENUM = {
    STACK: {
      id: ApplicationStatusEnum.STACK,
      label: t("candidates:candidates_send_to_stack_label"),
    },
    REJECTED: {
      id: ApplicationStatusEnum.REJECTED,
      label: t("candidates:candidates_send_to_rejected_label"),
    },
    SHORTLIST: {
      id: ApplicationStatusEnum.SHORTLIST,
      label: t("candidates:candidates_send_to_shortlist_label"),
    },
    INTERVIEW: {
      id: ApplicationStatusEnum.INTERVIEW,
      label: t("candidates:candidates_send_to_interview_label"),
    },
  };

  const { mutate, errors } = useMutationApplicationUpdate();
  const errorMsg = useServerErrorFormatter(errors);
  const { ErrorModal, show: showErrorModal } = useErrorModal(
    <>
      <p>{t("candidates:candidates_details_update_error")}</p>
      {errorMsg}
    </>
  );

  // Update selection when the list of applications passed to the component changes.
  // Beware, though, that this effect runs AFTER the render, which means that when an application is added,
  // there is a render where there won't be a value for this application in applicationsSelect.
  React.useEffect(() => {
    setSelection((prevSelection) => {
      const applicationsSelect = updateApplicationsSelectMap(
        applications,
        prevSelection.applicationsSelect
      );
      return {
        selectAll: calculateSelectAllStatus(applicationsSelect, prevSelection.selectAll),
        applicationsSelect: applicationsSelect,
      };
    });
  }, [applications]);

  const handleSelectAll = () => {
    setSelection(prevSelection => {
      const newApplicationSelection = {};
      Object.keys(prevSelection.applicationsSelect).forEach((element) => {
        newApplicationSelection[element] = !prevSelection.selectAll;
      });
      return ({
        selectAll: !prevSelection.selectAll,
        applicationsSelect: newApplicationSelection,
      });
    });
  };

  const handleApplicationSelect = (event) => {
    setSelection((prevSelection) => {

      // Update the status of the select application
      const applicationsSelect = {
        ...prevSelection.applicationsSelect,
        [event.target.name]: event.target.checked
      };

      return {
        selectAll: calculateSelectAllStatus(applicationsSelect, prevSelection.selectAll),
        applicationsSelect: applicationsSelect,
      };
    });
  };

  const handleChangeStatus = (status) => {
    Object.keys(selection.applicationsSelect).forEach((element) => {
      if (selection.applicationsSelect[element] === true) {
        const variables = {};
        variables["id"] = element;
        variables["status"] = status;
        mutate({ variables }).catch((/*error*/) => {
          showErrorModal();
        });
      }
    });
  };

  const dropdownItems = Object.keys(STATUS_ENUM).filter(
    (item) => STATUS_ENUM[item].id !== status
  );

  return (
    <>
      {ErrorModal}
      <div className={"ApplicationsList"}>
        <Row>
          <Col>
            <ThreeStateCheckbox
              checked={selection.selectAll}
              indeterminate={indeterminate}
              onChange={handleSelectAll}
              name="selectAll"
              label={t("candidates:candidates_select_all_label")}
            />
          </Col>
          <Col className="action-col">
            <Dropdown>
              <Dropdown.Toggle variant="success" id="dropdown-basic">
                {t("candidates:candidates_send_to_label")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {dropdownItems.map((item) => (
                  <Dropdown.Item
                    disabled={allUnchecked}
                    key={item}
                    onClick={() => handleChangeStatus(STATUS_ENUM[item].id)}
                  >
                    {STATUS_ENUM[item].label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        {applications.map((application, i) => (
          <Row key={i}>
            <Col className="widget-block">
              <ApplicationPreview
                application={application}
                selected={
                  selection.applicationsSelect[application._id] || false
                }
                handleApplicationSelect={handleApplicationSelect}
                onFilterChange={onFilterChange}
              />
            </Col>
          </Row>
        ))}
      </div>
    </>
  );
}

const isChecked = (currentValue) => currentValue === true;
const isUnchecked = (currentValue) => currentValue === false;

const calculateSelectAllStatus = (applicationsSelect, prevSelectAll) => {
  // Calculate the status of the selectAll checkbox wrt application checkboxes
  const applicationsSelectValues = Object.values(applicationsSelect);
  const allChecked = applicationsSelectValues.every(isChecked);
  const allUnchecked = applicationsSelectValues.every(isUnchecked);
  let selectAll = prevSelectAll;
  if (allChecked)
    selectAll = true;
  else if (allUnchecked)
    selectAll = false;
  return selectAll;
}
